import React from "react";

import { Container, Grid, Stack, Typography } from "@mui/material";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomStepper from "../../../../helper-components/form/CustomStepper";
import ConfirmationDialog from "../../../../helper-components/input/ConfirmationDialog";
import TransparentFragment from "../../../../helper-components/misc/TransparentFragment";
import ControlledAccordion from "../../../../helper-components/form/ControlledAccordion";
import CustomTooltip from "../../../../helper-components/misc/CustomTooltip";

import CustomSelection from "../../../../helper-components/form/CustomSelection";

import { useDialog } from "../../../../helper-functions/hooks/useDialog";
import { querySelectLibraries } from "../../../../helper-functions/api/libraryQueries";
import { querySelectResources } from "../../../../helper-functions/api/resourceQueries";
import { querySelectAuthors } from "../../../../helper-functions/api/authorQueries";
import { addFormToObj, getEllipsis, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { formHasErrors, formHasValue, validateRow } from "../../../../helper-functions/validation/objectValidation";

import { HIGHLIGHT_REQUEST_OBJECT } from "../../../../config/constants/Constants";
import { HIGHLIGHT_REQUEST_VALIDATION } from "../../../../helper-functions/validation/highlightValidation";

/**
 * A stepper component to add a new resource.
 * Includes helper descriptions and validation.
 */
export default function HighlightRequestAddStepper(props) {
    const { enqueueDialog, setOpen, ...options } = useDialog(); // Nested dialog component.

    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = HIGHLIGHT_REQUEST_OBJECT;
    const validation = HIGHLIGHT_REQUEST_VALIDATION;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            _max_length_secs: existingRow.constraints?.max_length_secs ?? 120,
            _min_timestamp_secs: existingRow.constraints?.min_timestamp_secs ?? 180,

            _recurring_process: existingRow.constraints?.recurring_process ?? true,

            _diversity_weight: existingRow.constraints?.diversity_weight ?? 0.1,
            _threshold: existingRow.constraints?.threshold ?? 0.15,

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        setValue(newRow, key, value);

        if (key === "_max_length_secs") {
            newRow.constraints.max_length_secs = isNaN(value) || !value ? null : parseInt(value);
        }

        if (key === "_min_timestamp_secs") {
            newRow.constraints.min_timestamp_secs = isNaN(value) || !value ? null : parseInt(value);
        }

        if (key === "_recurring_process") {
            newRow.constraints.recurring_process = value === "true" || value === true;
        }

        if (key === "_diversity_weight") {
            newRow.constraints.diversity_weight = isNaN(value) || !value ? null : parseFloat(value);
        }

        if (key === "_threshold") {
            newRow.constraints.threshold = isNaN(value) || !value ? null : parseFloat(value);
        }

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleClick = (confirm) => {
        const options = { row };

        callback(confirm, options);
    };

    const handleLibrary = (selected = []) => {
        const ids = selected.map((el) => el.id);
        const constraints = { ...row.constraints, library_ids: ids };
        onChange(row, "constraints", constraints);
    };

    const handleResources = (selected = []) => {
        const ids = selected.map((el) => el.id);
        const constraints = { ...row.constraints, resource_ids: ids };
        onChange(row, "constraints", constraints);
    };

    const handleAuthors = (selected = []) => {
        const ids = selected.map((el) => el.id);
        const constraints = { ...row.constraints, author_ids: ids };
        onChange(row, "constraints", constraints);
    };

    // TODO: does not accept only IDs, wants full data: selected={existingRow?.constraints?.author_ids ?? null}
    const accordionValues = [
        {
            title: "Library",
            description: "Select a library to search for highlights.",
            component: (
                <CustomSelection
                    {...props}
                    callback={handleLibrary}
                    query={querySelectLibraries}
                    getPrimarySearchText={(el) => el?.name}
                    getSecondarySearchText={(el) => el?.description}
                    getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                    getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                />
            )
        },
        {
            title: "Resources",
            description: "Select one or more resources to search for highlights.",
            component: (
                <CustomSelection
                    {...props}
                    multiselect
                    callback={handleResources}
                    query={querySelectResources}
                    getPrimarySearchText={(el) => el?.name}
                    getSecondarySearchText={(el) => el?.description}
                    getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                    getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                />
            )
        },
        {
            title: "Authors",
            description: "Select one or more authors to search for highlights.",
            component: (
                <CustomSelection
                    {...props}
                    multiselect
                    callback={handleAuthors}
                    query={querySelectAuthors}
                    getPrimarySearchText={(el) => el?.first_name}
                    getSecondarySearchText={(el) => el?.last_name}
                    getPrimaryText={(el) => `${el?.first_name} ${el?.last_name}`}
                    getSecondaryText={(el) => getEllipsis(el?.bio, 50)}
                />
            )
        }
    ];

    // Create the step components.
    const step1 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Add a name and a description for the new highlight request.

                            The name should be descriptive and unique, while the description should provide additional context about the request, detailing the specific theme or topic to search for.`}
                        />
                    </Grid>

                    <Grid item xs={12} mb={2}>
                        <CustomInput name="name" row={row} onChange={onChange} label="Name" autoFocus placeholder="Altruism" />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput
                            name="description"
                            row={row}
                            onChange={onChange}
                            label="Description"
                            multiline
                            minRows={4}
                            placeholder="Create highlights about the topic of altruism: how it's portrayed in culture, what are its implications, and also about the positive vision Rand had described."
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step2 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment value={`Define constraints and filters about the expected highlights.`} />
                    </Grid>

                    <Grid item xs={6}>
                        <CustomTooltip
                            form={<CustomInput name="_max_length_secs" row={row} onChange={onChange} label="Maximum length of clip (s)" type="number" />}
                            tooltip="The approximate maximum length of the highlight clip in seconds. The actual length may slightly vary based on the content."
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <CustomTooltip
                            form={<CustomInput name="_min_timestamp_secs" row={row} onChange={onChange} label="Minimum timestamp (s)" type="number" />}
                            tooltip="The minimum timestamp in seconds from which the highlight clip can start. This helps to filter out highlights that are too early in the resource."
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <CustomTooltip
                            form={<CustomInput name="_diversity_weight" row={row} onChange={onChange} label="Resource diversity weight" type="number" step={0.1} />}
                            tooltip="The resource diversity weight determines how much the search algorithm prioritizes using less-utilized resources. A higher value means more varied resources will be selected, while a lower value will favor strict transcript similarity to the query."
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <CustomTooltip
                            form={<CustomInput name="_threshold" row={row} onChange={onChange} label="Similarity threshold" step={0.1} />}
                            tooltip="Determines how similar the highlights must be to the query. A lower value means more results will be returned, while a higher value will filter out less relevant highlights."
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Stack direction="row" justifyContent="center" alignItems="center">
                            <CustomInput name="_recurring_process" row={row} onChange={onChange} checkbox />
                            <Typography variant="body2" color="textSecondary">
                                {"Process same request multiple times"}
                            </Typography>
                        </Stack>
                    </Grid>

                    <Grid item xs={12}>
                        <ControlledAccordion {...props} startClosed values={accordionValues} />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step3 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Creating new highlight request with the following details:
                            Name: ${row.name || "N/A"}
                            Description: ${row?.description?.slice(0, 50) + (row?.description?.length > 50 ? "..." : "")}
                            Libraries: ${row?.constraints?.library_ids?.length || "N/A"}
                            Resources: ${row?.constraints?.resource_ids?.length || "N/A"}
                            Authors: ${row?.constraints?.author_ids?.length || "N/A"}

                            Click "Apply" to save the request or "Reset" to start over.`}
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    // Define the steps and the final step.
    const steps = [
        {
            label: "Define new request",
            optional: false,
            component: step1(),
            nextDisabled: !formHasValue(row, "description") || formHasErrors(row)
        },
        {
            label: "Additional details",
            optional: false,
            component: step2(),
            nextDisabled: false
        }
    ];

    const final = { component: step3() };

    return (
        <>
            <ConfirmationDialog
                {...props}
                options={{
                    enqueueDialog,
                    setOpen,
                    ...options
                }}
            />

            <CustomStepper steps={steps} final={final} callback={(confirm) => handleClick(confirm)} />
        </>
    );
}
